import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ALU, IMAGES } from "../data/content";

export default function University() {
  return (
    <div data-testid="university-page">
      {/* ALU Crest Hero */}
      <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden" data-testid="alu-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.graduation} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/70" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5 flex justify-center md:justify-start"
          >
            <img
              src={ALU.logo}
              alt="Apostolos Leadership University crest"
              className="w-64 md:w-80 h-auto drop-shadow-[0_20px_45px_rgba(0,0,0,0.6)]"
              data-testid="alu-crest"
            />
          </motion.div>
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
              className="overline text-primary mb-6"
            >
              The Equipping Arm
            </motion.p>
            <h1 className="font-display uppercase text-foreground text-5xl sm:text-7xl lg:text-[7vw] leading-[0.85]">
              Apostolos<br />Leadership U.
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
              className="font-editorial italic text-2xl md:text-3xl text-secondary mt-8"
            >
              {ALU.motto}
            </motion.p>
            <p className="font-body text-muted-foreground max-w-xl mt-6 leading-relaxed">{ALU.intro}</p>
          </div>
        </div>
      </section>

      {/* President's Welcome */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20" data-testid="alu-president">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">A Welcome</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">
            Welcoming Our President
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <Reveal className="md:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 border border-secondary/40" />
              <img
                src={ALU.president.image}
                alt={ALU.president.name}
                className="relative w-full h-auto object-contain"
                data-testid="president-portrait"
              />
            </div>
            <div className="mt-6">
              <p className="font-display uppercase text-2xl text-foreground">{ALU.president.name}</p>
              <p className="overline text-secondary mt-2">{ALU.president.title}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <p className="font-editorial italic text-3xl md:text-5xl text-foreground leading-tight mb-10">
              “{ALU.president.pull}”
            </p>
            <div className="space-y-5">
              {ALU.president.message.map((p, i) => (
                <p key={i} className="font-body text-muted-foreground leading-relaxed">{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Programs — hover reveal rows */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20" data-testid="alu-programs">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">Schools & Programs</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">Formation for the Called</h2>
        </Reveal>
        <div className="border-t border-border relative">
          {ALU.programs.map((p, i) => (
            <div
              key={i}
              className="reveal-row group relative flex items-center justify-between py-10 border-b border-border hover:px-4 transition-all duration-500 cursor-default"
              data-testid={`program-${i}`}
            >
              <div className="flex items-baseline gap-6">
                <span className="overline text-secondary text-[0.65rem]">{p.code}</span>
                <span className="font-display uppercase text-2xl md:text-5xl text-foreground group-hover:text-secondary transition-colors duration-300">
                  {p.title}
                </span>
              </div>
              <ArrowUpRight className="text-muted-foreground group-hover:text-primary transition-colors" />
              <img
                src={p.image}
                alt=""
                className="reveal-thumb pointer-events-none absolute right-32 top-1/2 w-56 h-36 object-cover z-10 grayscale"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Degrees & Certifications */}
      <section className="px-6 md:px-10 max-w-[1600px] mx-auto py-20" data-testid="alu-degrees">
        <Reveal className="mb-12">
          <p className="overline text-primary mb-4">Academics</p>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-foreground">Degrees & Certifications</h2>
        </Reveal>

        <div className="border border-border">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-border bg-muted/40">
            <span className="col-span-6 overline text-secondary text-[0.6rem]">Program</span>
            <span className="col-span-4 overline text-secondary text-[0.6rem]">Credit Hours</span>
            <span className="col-span-2 overline text-secondary text-[0.6rem] text-right">Tuition</span>
          </div>
          {ALU.degrees.map((d, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 6) * 0.06 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-6 border-b border-border last:border-b-0 items-center group hover:bg-muted/30 transition-colors duration-300"
              data-testid={`degree-${i}`}
            >
              <div className="md:col-span-6">
                <span className="font-display uppercase text-xl md:text-2xl text-foreground group-hover:text-secondary transition-colors duration-300">
                  {d.name}
                </span>
              </div>
              <div className="md:col-span-4">
                <span className="font-body text-foreground">{d.credits}</span>
                {d.detail && <span className="block font-body text-sm text-muted-foreground mt-1">{d.detail}</span>}
              </div>
              <div className="md:col-span-2 md:text-right">
                <span className="font-display text-2xl text-secondary">{d.tuition}</span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border border-t-0">
          {ALU.academicNotes.map((note, i) => (
            <Reveal key={i} delay={i * 0.1} className="bg-background p-6">
              <span className="font-display text-secondary/40 text-3xl">{String(i + 1).padStart(2, "0")}</span>
              <p className="font-body text-muted-foreground leading-relaxed mt-3">{note}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Graduation CTA */}
      <section className="relative py-32 overflow-hidden mt-10" data-testid="alu-graduation">
        <div className="absolute inset-0">
          <img src={IMAGES.graduation} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/75" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-secondary mb-6">Class of 2026 · Oct 17</p>
            <h2 className="font-display uppercase text-5xl md:text-8xl text-foreground leading-[0.9] max-w-4xl">
              Commissioned & Sent
            </h2>
            <p className="font-body text-muted-foreground max-w-xl mt-8 leading-relaxed">
              Our ALU graduation ceremony crowns each cohort during the PeakDwellers Summit — presided by
              Apostle Dr. P. A. Williams and Overseer Cheryl McKinley.
            </p>
            <motion.div whileHover={{ x: 6 }} className="inline-block mt-10">
              <Link
                to="/contact"
                data-testid="alu-enroll-btn"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground overline px-8 py-5 hover:bg-primary/85 transition-colors"
              >
                Enroll / Inquire <ArrowUpRight size={18} />
              </Link>
            </motion.div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
